import React, { useState, useEffect, useRef } from 'react';
import { URLS } from '../_config';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import toast from 'react-hot-toast';
import { useUserApi } from '../store/api/hooks/userApiHook';
import { MenuIcon } from '../assets/Icons';
import { useUser } from '../store/hooks/authHook';
import { navigationWarningManager } from '../hooks/useNavigationWarning';

// Interface for dropdown items
interface DropdownItem {
    label: string;
    link: string;
}

// NavDropdown component for reusable dropdown menus
interface NavDropdownProps {
    label: string;
    items: DropdownItem[];
    isOpen: boolean;
    onToggle: () => void;
    dropdownRef: React.RefObject<HTMLLIElement>;
    onMobileMenuClose?: () => void;  // Add new prop
}

const NavDropdown: React.FC<NavDropdownProps> = ({ label, items, isOpen, onToggle, dropdownRef, onMobileMenuClose }) => {
    const navigate = useNavigate();
    
    const handleItemClick = async (link: string) => {
        // Check for active jobs before navigation
        if (navigationWarningManager.checkActiveJobs()) {
            const confirmed = await navigationWarningManager.showNavigationWarning();
            if (!confirmed) {
                return; // Don't navigate if user cancels
            }
        }
        
        onToggle(); // Close the dropdown
        if (onMobileMenuClose) {
            onMobileMenuClose(); // Close the mobile menu
        }
        // Use window.location.href for immediate navigation without beforeunload interference
        window.location.href = link;
    };
    
    return (
        <li className="relative" ref={dropdownRef}>
            <a className="flex items-center gap-1 py-2 px-3 text-white text-[15px] cursor-pointer whitespace-nowrap" onClick={onToggle}>
                {label}
                <svg
                    className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="white"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M6 9l6 6 6-6H6z" />
                </svg>
            </a>
            <ul className={`absolute left-0 top-full bg-white mt-0 w-48 shadow-md rounded-md z-10 ${isOpen ? 'block' : 'hidden'}`}>
                {items.map((item, index) => (
                    <li key={index}>
                        <a 
                            className="block px-4 py-2 text-gray-800 hover:bg-gray-100 cursor-pointer" 
                            onClick={() => handleItemClick(item.link)}
                        >
                            {item.label}
                        </a>
                    </li>
                ))}
            </ul>
        </li>
    );
};

// ProfileDropdown component for the profile menu
interface ProfileDropdownProps {
    username: string;
    isOpen: boolean;
    onToggle: () => void;
    onLogout: () => void;
    dropdownRef: React.RefObject<HTMLDivElement>;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ username, isOpen, onToggle, onLogout, dropdownRef }) => {
    const handleProfileClick = () => {
        onToggle(); // Close the dropdown when Profile is clicked
    };
    
    const handleLogoutClick = () => {
        onToggle(); // Close the dropdown when Logout is clicked
        onLogout();
    };
    
    return (
        <div className="relative" ref={dropdownRef}>
            <button
                className="flex items-center gap-2.5 bg-transparent border-none focus:outline-none"
                type="button"
                onClick={onToggle}
            >
                <img src="/assets/images/avatar.jpg" alt="Profile avatar" className="w-10 h-10 rounded-full border border-white" />
                <span className="text-white font-semibold">{username}</span>
                <svg className={`w-4 h-4 ml-1 transition-transform ${isOpen ? 'rotate-180' : ''} text-white`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                </svg>
            </button>
            <ul className={`absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg ${isOpen ? 'block' : 'hidden'} z-50`}>
                <li className="py-1 hover:bg-gray-100">
                    <a onClick={handleProfileClick} className="block px-4 py-2 text-gray-800 cursor-pointer">Profile</a>
                </li>
                <li className="py-1 hover:bg-gray-100">
                    <a onClick={handleLogoutClick} className="block px-4 py-2 text-gray-800 cursor-pointer">Logout</a>
                </li>
            </ul>
        </div>
    );
}

const Header: React.FC = () => {
    const navigate = useNavigate();
    const userApi = useUserApi();
    const user = useUser();
    
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [openDropdowns, setOpenDropdowns] = useState<{ [key: string]: boolean }>({
        profile: false,
        client: false,
        wineMatch: false,
        rollup: false,
        download: false,
        wineAddition: false,
        noise: false,
        siteSettings: false,
    });
    //const user: any = useUserSelector();

    // Helper function to handle navigation with warning check
    const handleNavigateWithWarning = async (url: string) => {
        if (navigationWarningManager.checkActiveJobs()) {
            const confirmed = await navigationWarningManager.showNavigationWarning();
            if (!confirmed) {
                return; // Don't navigate if user cancels
            }
        }
        setMobileMenuOpen(false); // Close mobile menu
        // Use window.location.href for immediate navigation without beforeunload interference
        window.location.href = url;
    };

    // Create refs for all dropdown containers
    const profileRef = useRef<HTMLDivElement>(null);
    const clientRef = useRef<HTMLLIElement>(null);
    const wineMatchRef = useRef<HTMLLIElement>(null);
    const rollupRef = useRef<HTMLLIElement>(null);
    const downloadRef = useRef<HTMLLIElement>(null);
    const wineAdditionRef = useRef<HTMLLIElement>(null);
    const noiseRef = useRef<HTMLLIElement>(null);
    const siteSettingsRef = useRef<HTMLLIElement>(null);

    // Effect to handle click outside the dropdowns
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            // Check profile dropdown
            if (profileRef.current && !profileRef.current.contains(event.target as Node) && openDropdowns.profile) {
                setOpenDropdowns(prev => ({ ...prev, profile: false }));
            }
            // Check client dropdown
            if (clientRef.current && !clientRef.current.contains(event.target as Node) && openDropdowns.client) {
                setOpenDropdowns(prev => ({ ...prev, client: false }));
            }
            // Check wineMatch dropdown
            if (wineMatchRef.current && !wineMatchRef.current.contains(event.target as Node) && openDropdowns.wineMatch) {
                setOpenDropdowns(prev => ({ ...prev, wineMatch: false }));
            }
            // Check rollup dropdown
            if (rollupRef.current && !rollupRef.current.contains(event.target as Node) && openDropdowns.rollup) {
                setOpenDropdowns(prev => ({ ...prev, rollup: false }));
            }
            // Check download dropdown
            if (downloadRef.current && !downloadRef.current.contains(event.target as Node) && openDropdowns.download) {
                setOpenDropdowns(prev => ({ ...prev, download: false }));
            }
            // Check wineAddition dropdown
            if (wineAdditionRef.current && !wineAdditionRef.current.contains(event.target as Node) && openDropdowns.wineAddition) {
                setOpenDropdowns(prev => ({ ...prev, wineAddition: false }));
            }
            // Check noise dropdown
            if (noiseRef.current && !noiseRef.current.contains(event.target as Node) && openDropdowns.noise) {
                setOpenDropdowns(prev => ({ ...prev, noise: false }));
            }
            // Check siteSettings dropdown
            if (siteSettingsRef.current && !siteSettingsRef.current.contains(event.target as Node) && openDropdowns.siteSettings) {
                setOpenDropdowns(prev => ({ ...prev, siteSettings: false }));
            }
        };

        // Add event listener
        document.addEventListener('mousedown', handleClickOutside);

        // Clean up event listener
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdowns]);

    const toggleDropdown = (dropdown: string) => {
        setOpenDropdowns({
            ...openDropdowns,
            [dropdown]: !openDropdowns[dropdown]
        });
    };

    const handleLogout = () => {
        Swal.fire({
            title: 'Logout',
            text: 'Do you want to logout from all devices?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, all devices',
            cancelButtonText: 'No, just this device',
        }).then((result: any) => {
            if (result.isConfirmed) {
                logout(true);
            } else {
                logout(false);
            }
        });
    };

    // Accepts a boolean param for all devices
    const logout = (allDevices: boolean = false) => {
        try {
            userApi.logout(allDevices, () => {
                navigate(URLS.AUTH.SIGNIN);
                toast.success('User Logged Out Successfully');
            });
        } catch (e: any) {
            console.log('error =>', e);
            toast.error(e?.message);
        }
    }

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!mobileMenuOpen);
    };

    const getFirstName = () => {
        if (user && user.data.first_name) return user.data.first_name;
        if (user && user.data.name) return user.data.name.split(' ')[0];

        // fallback to localStorage if redux is empty
        const storage = localStorage.getItem('wine-data-systems');
        if (storage) {
            try {
                const parsed = JSON.parse(storage);
                if (parsed && parsed.user) {
                    const userObj = JSON.parse(parsed.user);
                    if (userObj.first_name) return userObj.first_name;
                    if (userObj.name) return userObj.name.split(' ')[0];
                }
            } catch {}
        }
        return '';
    };

    console.log(user, 'user in header');

    return (
        <div className="sticky top-0 left-0 right-0 z-50 w-full max-w-full">
            {/* Logo Panel - Commented Out 
            <div className="bg-[#D3D3D3] py-2 px-2 border-t-5 border-t-black w-full max-w-full">
                <div className="mx-auto w-full max-w-full">
                    <div className="flex justify-between items-center min-w-0">
                        <Link className="flex items-center min-w-0" to={URLS.JOB_LOG.LIST}>
                            <img src="/assets/images/logo.png" alt="Logo" className="max-h-[50px] w-auto" />
                        </Link>
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="relative min-w-0">
                                <img src="/assets/images/bell-icon.svg" alt="Notification Bell Icon" className="w-[30px]" />
                                <span className="absolute -top-1 -right-1 w-5 h-5 flex justify-center items-center bg-[#D22630] text-white text-xs rounded-full cursor-pointer">0</span>
                            </div>
                            <ProfileDropdown
                                username={getFirstName()}
                                isOpen={openDropdowns.profile}
                                onToggle={() => toggleDropdown('profile')}
                                onLogout={handleLogout}
                                dropdownRef={profileRef}
                            />
                        </div>
                    </div>
                </div>
            </div>
            */}
            {/* Navigation Menu */}
            <div className="bg-[#D22630] shadow-md py-2 w-full max-w-full">
                <nav className="mx-auto w-full max-w-full">
                    <div className="relative w-full max-w-full flex justify-between items-center">
                        <div className="flex items-center">
                            <button
                                className="nav:hidden px-2 py-1 text-white"
                                type="button"
                                onClick={toggleMobileMenu}
                            >
                                <MenuIcon className="w-6 h-6" />
                            </button>
                            <div className={`${mobileMenuOpen ? 'block' : 'hidden'} nav:block absolute nav:relative bg-[#D22630] w-full nav:w-auto left-0 top-full nav:top-auto z-40 max-w-full`}>
                                <ul className="flex flex-col nav:flex-row nav:flex-nowrap min-w-0 w-full max-w-full">
                                    {/* <li>
                                        <a
                                            className="block py-2 px-3 text-white text-[15px] whitespace-nowrap"
                                            href={URLS.JOB_LOG.LIST}
                                            onClick={e => { e.preventDefault(); handleNavigateWithWarning(URLS.JOB_LOG.LIST); }}
                                        >
                                            Job-Logs
                                        </a>
                                    </li> */}
                                <NavDropdown
                                    label="Client"
                                    items={[
                                        { label: 'Client', link: URLS.CRAWLER.LIST },
                                        { label: 'Group Crawler', link: URLS.CRAWLER.RETAILER_GROUP.LIST }
                                    ]}
                                    isOpen={openDropdowns.client}
                                    onToggle={() => toggleDropdown('client')}
                                    dropdownRef={clientRef}
                                    onMobileMenuClose={() => setMobileMenuOpen(false)}
                                />
                                <li>
                                    <a
                                        className="block py-2 px-3 text-white text-[15px] whitespace-nowrap"
                                        href={URLS.WORK_GROUP.LIST}
                                        onClick={async (e) => { 
                                            e.preventDefault(); 
                                            await handleNavigateWithWarning(URLS.WORK_GROUP.LIST); 
                                        }}
                                    >
                                        Work Group
                                    </a>
                                </li>
                                <NavDropdown
                                    label="Wine Match"
                                    items={[
                                        { label: 'Match Config', link: URLS.WINE_MATCH.MATCH_CONFIG.LIST },
                                        { label: 'History Match', link: URLS.WINE_MATCH.HISTORY.LIST },
                                        { label: 'Keyword Match', link: URLS.WINE_MATCH.KEYWORD.LIST }
                                    ]}
                                    isOpen={openDropdowns.wineMatch}
                                    onToggle={() => toggleDropdown('wineMatch')}
                                    dropdownRef={wineMatchRef}
                                    onMobileMenuClose={() => setMobileMenuOpen(false)}
                                />
                                <NavDropdown
                                    label="Rollup"
                                    items={[
                                        { label: 'RP Rollup', link: URLS.ROLLUP.RP.LIST },
                                        { label: 'WA Rollup', link: URLS.ROLLUP.WA.LIST }
                                    ]}
                                    isOpen={openDropdowns.rollup}
                                    onToggle={() => toggleDropdown('rollup')}
                                    dropdownRef={rollupRef}
                                    onMobileMenuClose={() => setMobileMenuOpen(false)}
                                />
                                <NavDropdown
                                    label="Database"
                                    items={[
                                        { label: 'Download', link: URLS.DOWNLOAD.LIST },
                                        { label: 'Upload', link: URLS.DOWNLOAD.UPLOAD }
                                    ]}
                                    isOpen={openDropdowns.download}
                                    onToggle={() => toggleDropdown('download')}
                                    dropdownRef={downloadRef}
                                    onMobileMenuClose={() => setMobileMenuOpen(false)}
                                />
                                <li>
                                    <a
                                        className="block py-2 px-3 text-white text-[15px] whitespace-nowrap"
                                        href={URLS.VALIDATION.GET}
                                        onClick={async (e) => { 
                                            e.preventDefault(); 
                                            await handleNavigateWithWarning(URLS.VALIDATION.GET); 
                                        }}
                                    >Validation</a>
                                </li>
                                <li>
                                    <a
                                        className="block py-2 px-3 text-white text-[15px] whitespace-nowrap"
                                        href={URLS.NARROW.GET}
                                        onClick={async (e) => { 
                                            e.preventDefault(); 
                                            await handleNavigateWithWarning(URLS.NARROW.GET); 
                                        }}
                                    >Narrow</a>
                                </li>
                                <NavDropdown
                                    label="Wine Addition"
                                    items={[
                                        { label: 'External Input', link: URLS.WINE_ADDITION.EXTERNAL_INPUT.CREATE },
                                        { label: 'Direct Input', link: URLS.WINE_ADDITION.DIRECT_INPUT.CREATE },
                                        { label: 'Keyword Generator', link: URLS.WINE_ADDITION.KEYWORD_GENERATOR.CREATE }
                                    ]}
                                    isOpen={openDropdowns.wineAddition}
                                    onToggle={() => toggleDropdown('wineAddition')}
                                    dropdownRef={wineAdditionRef}
                                    onMobileMenuClose={() => setMobileMenuOpen(false)}
                                />
                                <li>
                                    <a
                                        className="block py-2 px-3 text-white text-[15px] whitespace-nowrap"
                                        href={URLS.MATCHED.LIST}
                                        onClick={async (e) => { 
                                            e.preventDefault(); 
                                            await handleNavigateWithWarning(URLS.MATCHED.LIST); 
                                        }}
                                    >Matched</a>
                                </li>
                                <NavDropdown
                                    label="Noise"
                                    items={[
                                        { label: 'Noise Parser', link: URLS.NOISE.NOISE_PARSER.LIST },
                                        { label: 'Global Noise', link: URLS.NOISE.GLOBAL.LIST },
                                        { label: 'Wine Noise', link: URLS.NOISE.WINE_NOISE.LIST },
                                        { label: 'Producer Noise', link: URLS.NOISE.PRODUCER_NOISE.LIST }
                                    ]}
                                    isOpen={openDropdowns.noise}
                                    onToggle={() => toggleDropdown('noise')}
                                    dropdownRef={noiseRef}
                                    onMobileMenuClose={() => setMobileMenuOpen(false)}
                                />
                                <NavDropdown
                                    label="Site-Settings"
                                    items={[
                                        { label: 'Job Log', link: URLS.JOB_LOG.LIST },
                                        { label: 'Country', link: URLS.LOCATION_SETTINGS.COUNTRY.LIST },
                                        { label: 'Region', link: URLS.LOCATION_SETTINGS.REGION.LIST },
                                        { label: 'Sub Region', link: URLS.LOCATION_SETTINGS.SUB_REGION.LIST },
                                        { label: 'Appellation', link: URLS.LOCATION_SETTINGS.APPELLATION.LIST },
                                        { label: 'Sub Appellation', link: URLS.LOCATION_SETTINGS.SUB_APPELLATION.LIST },
                                        // { label: 'Location', link: URLS.LOCATION_SETTINGS.LOCATION.LIST },
                                        // { label: 'Locales', link: URLS.LOCATION_SETTINGS.LOCALES.LIST },
                                        { label: 'Varieties', link: URLS.LOCATION_SETTINGS.VARIETIES.LIST }
                                    ]}
                                    isOpen={openDropdowns.siteSettings}
                                    onToggle={() => toggleDropdown('siteSettings')}
                                    dropdownRef={siteSettingsRef}
                                    onMobileMenuClose={() => setMobileMenuOpen(false)}
                                />
                                </ul>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 min-w-0 mr-4">
                            {/* Notification Bell */}
                            <div className="relative min-w-0">
                                <img src="/assets/images/bell-icon.svg" alt="Notification Bell Icon" className="w-[30px]" />
                                <span className="absolute -top-1 -right-1 w-5 h-5 flex justify-center items-center bg-white text-[#D22630] text-xs rounded-full cursor-pointer font-semibold">0</span>
                            </div>
                            {/* Profile Avatar Dropdown */}
                            <ProfileDropdown
                                username={getFirstName()}
                                isOpen={openDropdowns.profile}
                                onToggle={() => toggleDropdown('profile')}
                                onLogout={handleLogout}
                                dropdownRef={profileRef}
                            />
                        </div>
                    </div>
                </nav>
            </div>
        </div>
    );
};

export default Header;